import React, { useState } from "react";
import type { ProjectStore, Role } from "../stores/ProjectStore";

interface Props {
  store: ProjectStore;
}

export function AuthorizedAccountsSettings({ store }: Props) {
  const [open, setOpen] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [role, setRole] = useState<Role>("writer");

  if (!open) {
    return (
      <button
        type="button"
        className="api-key-settings-toggle"
        onClick={() => {
          setOpen(true);
        }}
        title="Manage shared access"
      >
        Shared Access
      </button>
    );
  }

  const entries = store.listAuthorizedAccounts();

  function handleAdd() {
    const trimmed = accountId.trim();
    if (!trimmed) return;
    // Clear only on a real add. A duplicate id leaves the text in place, so
    // the field emptying still means "that worked".
    void store.authorizeAccount(trimmed, role).then((added) => {
      if (added) setAccountId("");
    });
  }

  function handleRemove(id: string) {
    void store.revokeAccount(id);
  }

  return (
    <div className="authorized-accounts-panel">
      <div className="authorized-accounts-header">
        <span className="authorized-accounts-title">Shared Access</span>
        <button
          type="button"
          className="api-key-settings-cancel"
          onClick={() => {
            setOpen(false);
          }}
          title="Close the shared access panel"
        >
          Close
        </button>
      </div>
      <p className="authorized-accounts-hint">
        Grant other Jazz accounts access to all your projects. Use this to
        connect fuse-mirror or share with collaborators.
      </p>

      {entries.length > 0 && (
        <div className="authorized-accounts-list">
          {entries.map((entry) => (
            <div key={entry.accountId} className="authorized-account-row">
              <span className="authorized-account-id">{entry.accountId}</span>
              <span className={`role-badge role-${entry.role}`}>
                {entry.role}
              </span>
              <button
                type="button"
                className="delete-btn"
                onClick={() => {
                  handleRemove(entry.accountId);
                }}
                title="Remove access"
              >
                x
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="authorized-accounts-add-row">
        <input
          className="api-key-settings-input"
          type="text"
          value={accountId}
          onChange={(e) => {
            setAccountId(e.target.value);
          }}
          placeholder="co_z... (account ID)"
        />
        <select
          className="authorized-accounts-role-select"
          value={role}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "reader" || val === "writer" || val === "admin") {
              setRole(val);
            }
          }}
        >
          <option value="reader">reader</option>
          <option value="writer">writer</option>
          <option value="admin">admin</option>
        </select>
        <button
          type="button"
          className="api-key-settings-save"
          onClick={handleAdd}
          disabled={!accountId.trim()}
          title="Grant this account access to all your projects"
        >
          Add
        </button>
      </div>
    </div>
  );
}
